/* eslint-disable max-lines */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MortgageApplicationNavigationEvents } from '../../../../lib/constants/mortgage-application-navigation-events';
import { ReferenceDataLabels } from '../../../../lib/constants/reference-data-labels';
import { ReferenceDataOptionLists } from '../../../../lib/constants/reference-data-option-lists';
import { UserPermissions } from '../../../../lib/constants/user-permissions';
import { AffordabilityResultsDto, ValidationError } from '../../../../lib/index';
import { ContextualHelpNotifierService } from '../../../../lib/services/contextual-help-notifier.service';
import { BaseFormComponent } from '../../../shared/components/base-form.component';
import { MortgageApplicationRouterService } from '../../services/mortgage-application-router.service';
import { MortgageApplicationService } from '../../services/mortgage-application.service';
import { TabNavigationService } from '../../services/tab-navigation.service';
import { ReferenceDataService } from './../../../../lib/services/reference-data.service';
import { RomeNotificationService } from './../../../../lib/services/rome-notification.service';
import { Applicant, MortgageApplication, MortgageApplicationResponse } from './../../index';
import { MortgageApplicationStore } from './../../services/mortgage-application-store';

@Component({
  selector: 'boi-mortgage-application',
  templateUrl: './mortgage-application.page.html',
  styleUrls: ['./../../../shared/styles/application.page.scss', './mortgage-application.page.scss']
})
export class MortgageApplicationPage extends BaseFormComponent implements OnInit, OnDestroy {
  private readonly destroySubject$ = new Subject();

  public readonly userPermissions = UserPermissions;

  public mortgageApplication: MortgageApplication;
  public referenceDataLabels = ReferenceDataLabels;
  public mortgageApplicationNavigationEvents = MortgageApplicationNavigationEvents;
  public applicationId: string;
  public sections: any;
  public activeTab: any;
  public affordabilityResults: AffordabilityResultsDto;
  public affordabilityValidationErrors: Array<ValidationError>;
  public canCheckAffordability = false;
  public canAddApplicant = true;

  public isAffordabilityDialogOpened = false;
  public isAffordabilityWarningDialogOpened = false;
  public isHardCreditWarningDialogOpened = false;
  public isSoftCreditWarningDialogOpened = false;
  public isApplicantOrderWarningDialogOpened = false;
  public isFeesKfiNotFoundDialogOpened = false;
  public isFactFind2Visible: boolean;
  public hasSubmitValidationError: boolean;

  public readonly MORTGAGE_TAB = 'yourMortgage';
  public readonly APPLICANT_TAB = 'applicantTab';
  public readonly OUTGOINGS_TAB = 'yourOutgoings';
  public readonly PROPERTY_TAB = 'yourProperty';

  constructor(
    protected readonly activeRoute: ActivatedRoute,
    protected readonly mortgageApplicationStore: MortgageApplicationStore,
    protected readonly mortgageApplicationService: MortgageApplicationService,
    public router: Router,
    public tabNavigationService: TabNavigationService,
    protected readonly referenceDataService: ReferenceDataService,
    protected readonly romeNotificationService: RomeNotificationService,
    protected readonly mortgageApplicationRouterService: MortgageApplicationRouterService,
    protected readonly contextualHelpNotifierService: ContextualHelpNotifierService
  ) {
    super(router);
    this.tabNavigationService.event
      .pipe(
        filter((event) => event && event.type === MortgageApplicationNavigationEvents.ChangeMortgageAppTab),
        takeUntil(this.destroySubject$)
      )
      .subscribe((event) => {
        this.contextualHelpNotifierService.contextualHelpChanged(undefined);
        this.activeTab = {
          key: event.data['tab'],
          index: event.data['index']
        };
      });
    this.tabNavigationService.event.pipe(takeUntil(this.destroySubject$)).subscribe((event) => {
      if (event.type === MortgageApplicationNavigationEvents.AddApplicant) {
        this.addApplicant();
      } else if (event.type === MortgageApplicationNavigationEvents.DeleteApplicant) {
        this.deleteApplicant(event.data);
      }
    });
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.tabNavigationService.emitEvent({
        type: MortgageApplicationNavigationEvents.ChangeMortgageAppTab,
        data: this.getTabNavigationDataFromUrl()
      });
    });
  }

  ngOnInit(): void {
    const resolver = this.activeRoute.snapshot.data['mortgageApplicationDataResolver'];
    const mortgageApplicationResponse = resolver['mortgageApplicationResponse'] as MortgageApplicationResponse;
    this.applicationId = this.activeRoute.snapshot.params['applicationId'];

    this.mortgageApplicationStore.dataObservable
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((data: MortgageApplicationResponse) => {
        this.hasSubmitValidationError =
          data.hasSubmitValidationError !== undefined ? data.hasSubmitValidationError : this.hasSubmitValidationError;
        this.mortgageApplication = data.mortgageApplicationEntity;
        this.sections = data.sections;
        this.canCheckAffordability = !!this.mortgageApplication && this.isCheckAffordabilityAvailable();
        this.canAddApplicant = this.canApplicantBeAddedToApplication();
      });
    this.mortgageApplicationStore.isSubmitted = false;
    this.mortgageApplicationStore.setInitialMortgageApplicationResponseWithEditableFlags(
      mortgageApplicationResponse,
      resolver['editableAppFlags']
    );
    this.mortgageApplicationStore.setLoanTypes(resolver['allLoanTypes'].LoanTypes);
    this.mortgageApplicationStore.mortgageClubs = resolver['mortgageClubs'];

    this.isFactFind2Visible = this.mortgageApplication.isFactFind2Visible();
  }

  public getLabel(applicant: Applicant): string {
    return [applicant.Forename, applicant.Surname].filter(Boolean).join(' ');
  }

  public goToDeclaration(): void {
    this.validateMortgageApplication(() => {
      if (this.mortgageApplication.isAdvisedCase) {
        this.checkIfAnyFeesKfiExistsInCase();
      } else {
        this.proceedToDeclaration();
      }
    });
  }

  public submitAip(): void {
    this.validateMortgageApplication(() => {
      this.openHardCreditWarningDialog();
    });
  }

  public validateFactFind1(): void {
    this.validateMortgageApplication(() => {
      if (this.mortgageApplication.IsFactFindApplicantOrderChangeAfterSam) {
        this.openApplicantOrderWarningDialog();
      } else {
        this.openSoftCreditWarningDialog();
      }
    });
  }

  public performAffordability(): void {
    this.mortgageApplicationService
      .performAdhocAffordabilityCheck(this.mortgageApplication)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((response) => {
        if (response.ValidationErrors.FullValidationErrors.length > 1) {
          this.affordabilityValidationErrors = response.ValidationErrors.FullValidationErrors.filter(
            (err) => err.name !== undefined
          );
          this.isAffordabilityWarningDialogOpened = true;
        } else {
          this.affordabilityResults = response.Entity;
          this.isAffordabilityDialogOpened = true;
        }
      });
  }

  protected openHardCreditWarningDialog(): void {
    this.isHardCreditWarningDialogOpened = true;
  }

  public openSoftCreditWarningDialog(): void {
    this.isSoftCreditWarningDialogOpened = true;
  }

  private openApplicantOrderWarningDialog(): void {
    this.isApplicantOrderWarningDialogOpened = true;
  }

  private openFeesKfiNotFoundDialog(): void {
    this.isFeesKfiNotFoundDialogOpened = true;
  }

  public submitApplication(): void {
    this.mortgageApplicationService
      .submitMortgageApplication(this.getSubmitPreparedApplication())
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((mortgageApplicationResponse: MortgageApplicationResponse) => {
        this.updateMortgageApplicationResponse(mortgageApplicationResponse);
      });
  }

  public submitFactFind1(): void {
    this.mortgageApplicationService
      .saveFactFind1(this.getSubmitPreparedApplication())
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((mortgageApplicationResponse: MortgageApplicationResponse) => {
        this.updateMortgageApplicationResponse(mortgageApplicationResponse);
      });
  }

  public isApplicantOrderVisible(): boolean {
    return this.mortgageApplication.TransactNumber > 0;
  }

  private updateMortgageApplicationResponse(mortgageApplicationResponse: MortgageApplicationResponse): void {
    const sections = this.mortgageApplicationStore.mortgageApplicationResponse.sections;
    mortgageApplicationResponse.isSubmitted = true;
    mortgageApplicationResponse.sections = sections;

    this.mortgageApplicationStore.mortgageApplicationResponse = mortgageApplicationResponse;

    if (mortgageApplicationResponse.validationErrors.BusinessValidationErrors.length > 1) {
      this.romeNotificationService.showError(
        this.referenceDataService.getLocalizeLabel(this.referenceDataLabels.NotificationValidationErrors)
      );
    }

    if (mortgageApplicationResponse.validationErrors.FullValidationErrors.length === 1) {
      this.goToCaseSummary();
    }
  }

  public ngOnDestroy(): void {
    this.destroyStoreData();
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  public goToCaseSummary(): void {
    this.mortgageApplicationRouterService.goToCaseSummary(this.mortgageApplication.ApplicationCase.CaseNumberAsHash);
  }

  private destroyStoreData(): void {
    this.mortgageApplicationStore.availableLoanTypes = undefined;
    this.mortgageApplicationStore.mortgageApplicationResponse.hasSubmitValidationError = undefined;
  }

  protected validateMortgageApplication(validationCallback: () => void): void {
    this.mortgageApplicationService.prepareDataForSave(this.mortgageApplication);

    this.mortgageApplication.CheckConsentValidation = false;
    this.mortgageApplicationService
      .validateMortgageApplication(this.mortgageApplication)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(
        (validatedMortgageApp) => this.successValidationHandler(validatedMortgageApp, validationCallback),
        () => this.errorValidationHandler()
      );
  }

  private successValidationHandler(
    validatedMortgageApp: MortgageApplicationResponse,
    validationCallback: () => void
  ): void {
    if (validatedMortgageApp) {
      this.mortgageApplicationStore.mortgageApplicationResponse = validatedMortgageApp;
      validationCallback();
    } else {
      const localizedMessage = this.referenceDataService.getLocalizeLabel(
        this.referenceDataLabels.NotificationValidationErrors
      );
      this.romeNotificationService.showError(localizedMessage);
    }
  }

  private errorValidationHandler(): void {
    this.mortgageApplicationService
      .loadMortgageApplication(this.applicationId)
      .subscribe((mortgageApplicationResponse) => {
        mortgageApplicationResponse.isSubmitted = true;
        this.mortgageApplicationStore.mortgageApplicationResponse = mortgageApplicationResponse;
      });
  }

  private checkIfAnyFeesKfiExistsInCase(): void {
    this.mortgageApplicationService
      .checkIfAnyKfiForCalculatingFees(this.applicationId)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((feesCalculated) => {
        if (feesCalculated) {
          this.proceedToDeclaration();
        } else {
          this.openFeesKfiNotFoundDialog();
        }
      });
  }

  public proceedToDeclaration(): void {
    this.mortgageApplicationRouterService.goToDeclaration(this.applicationId);
  }

  private isCheckAffordabilityAvailable(): boolean {
    return (
      [
        ReferenceDataOptionLists.AdvisedStatusFF1Incomplete,
        ReferenceDataOptionLists.AdvisedStatusFF1Declined,
        ReferenceDataOptionLists.AdvisedStatusFF1Complete,
        ReferenceDataOptionLists.AdvisedStatusWithAdvisor,
        ReferenceDataOptionLists.AdvisedStatusAwaitingCustomer,
        ReferenceDataOptionLists.AdvisedStatusRecommendationMade
      ].indexOf(this.mortgageApplication.StatusCode) > -1
    );
  }

  public submitFactFind2(): void {
    const isApplicationReadonly =
      this.mortgageApplicationStore.mortgageApplicationResponse.mortgageApplicationEntity.IsApplicationReadOnly;
    const isFactFindReadonly =
      this.mortgageApplicationStore.mortgageApplicationResponse.mortgageApplicationEntity.IsFactFindReadOnly;
    this.mortgageApplicationService
      .saveFactFind2(this.getSubmitPreparedApplication())
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((response) => {
        if (response.validationErrors.FullValidationErrors.length === 1) {
          this.handleSubmitFactFind2Success(response);
        } else {
          this.handleSubmitFactFind2ValidationError(response, isApplicationReadonly, isFactFindReadonly);
        }
      });
  }

  private handleSubmitFactFind2Success(response: MortgageApplicationResponse): void {
    response.hasSubmitValidationError = false;
    const localizedMessage = this.referenceDataService.getLocalizeLabel(
      this.referenceDataLabels.NotificationSaveSuccess
    );
    this.romeNotificationService.showSuccess(localizedMessage);
    void this.router.navigate([
      '/caseOverview',
      this.mortgageApplication.ApplicationCase.CaseNumberAsHash,
      'caseAdvice'
    ]);
  }

  private handleSubmitFactFind2ValidationError(
    response: MortgageApplicationResponse,
    isApplicationReadonly: boolean,
    isFactFindReadonly: boolean
  ): void {
    response.hasSubmitValidationError = true;
    response.isSubmitted = true;
    response.mortgageApplicationEntity.IsApplicationReadOnly = isApplicationReadonly;
    response.mortgageApplicationEntity.IsFactFindReadOnly = isFactFindReadonly;
    this.mortgageApplicationStore.mortgageApplicationResponse = response;

    const localizedMessage = this.referenceDataService.getLocalizeLabel(
      this.referenceDataLabels.NotificationValidationErrors
    );
    this.romeNotificationService.showError(localizedMessage);
  }

  private getSubmitPreparedApplication(): MortgageApplication {
    const application = { ...this.mortgageApplication } as any;
    this.mortgageApplicationService.prepareDataForSubmit(application);

    return application;
  }

  private canApplicantBeAddedToApplication(): boolean {
    return (
      !!this.mortgageApplication &&
      this.mortgageApplication.Applicants.length < 4 &&
      !this.mortgageApplication.IsApplicationReadOnly &&
      !(this.mortgageApplication.IsFactFind && this.mortgageApplication.IsFactFindReadOnly)
    );
  }

  addApplicant(): void {
    const applicant = new Applicant();
    applicant.ApplicantTransactOrder = this.mortgageApplication.Applicants.length + 1;
    this.mortgageApplication.Applicants.push(applicant);
    this.canAddApplicant = this.canApplicantBeAddedToApplication();
    this.navigateToLastApplicant();
  }

  private navigateToLastApplicant(): void {
    const lastApplicantIndex = this.mortgageApplication.Applicants.length - 1;
    void this.router.navigate(['applicant', lastApplicantIndex], { relativeTo: this.activeRoute });
    this.tabNavigationService.emitEvent({
      type: MortgageApplicationNavigationEvents.ChangeMortgageAppTab,
      data: { tab: 'applicant', index: lastApplicantIndex }
    });
  }

  private deleteApplicant(applicantIndex: number): void {
    this.mortgageApplication.Applicants.splice(applicantIndex, 1);
    this.navigateToLastApplicant();
    this.saveApplication();
  }

  private saveApplication(): void {
    this.mortgageApplicationService
      .saveMortgageApplication(this.mortgageApplicationStore.mortgageApplicationResponse.mortgageApplicationEntity)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((data) => {
        this.mortgageApplicationStore.mortgageApplicationResponse = data;
      });
  }

  public trackByApplicantTransactOrder(index: number, applicant: Applicant): number {
    return applicant.ApplicantTransactOrder;
  }

  public showValidateFactFind1Btn(): boolean {
    return (
      this.activeTab.key !== 'factFind2' &&
      this.mortgageApplication?.IsFactFind &&
      !this.mortgageApplication?.IsFactFindReadOnly &&
      !this.mortgageApplication?.IsApplicationReadOnly
    );
  }
}
